// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { Dropdown } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import { DateTime, Duration } from "luxon";
import { createSelector } from "@reduxjs/toolkit";
import { TimeUnit } from "chart.js";
import { ITrendData } from "states/trendingstates";

import {
  DashboardPanel,
  DataState,
  EnumTypeString,
  IDashboardEntry,
  INavigationPanelEntry,
  MainPanel,
  RootPath,
  Theme,
  TimePeriod,
} from "types";
import { isSet, getTimezoneDiff, getBrowserTimezoneOffset, roundToMinDigits } from "utils";
import { Aggregation } from "graphql/api";
import { IActionSortTable } from "actions";
import { EnergySource, EnergyUnit, InputType, OutputMode, PowerUnit } from "parameter";
import { IState } from "states/states";
import { ITableSort } from "states/guistates";
import { IPanelSelection, GraphViewMode } from "states/cachedstates";
import { IStateController } from "states/controllerstates";

import cssContent from "content.scss";

// uses webpack definitions
declare const IS_CLOUD: boolean;

enum GraphTickTimeSpans {
  MONTHS = 3 * 28 * 60 * 60 * 24,
  DAYS = 28 * 60 * 60 * 24,
  HOURS = 2 * 60 * 60 * 24,
  MINUTES = 60 * 60 * 24,
}

export const MAX_DASHBOARDS = 6;

export const COLOR_SELECT = "#66C0CC"; // "#70adbc"

const DEFAULT_COLORS = [
  cssContent.GRAPH_COLOR,
  "#3366CC",
  "#FF9900",
  "#109618",
  "#990099",
  "#3B3EAC",
  "#0099C6",
  "#66AA00",
  "#316395",
  "#994499",
  "#22AA99",
  "#AAAA11",
  "#6633CC",
  "#E67300",
  "#329262",
  "#5574A6",
  "#3B3EAC",
];

export function getColor(index: number | undefined) {
  return DEFAULT_COLORS[(index ?? 0) % DEFAULT_COLORS.length];
}

export function parseHtmlInputValue(
  inputElement: HTMLInputElement,
): string | boolean | number | Date {
  if (inputElement.type === "checkbox") {
    return inputElement.checked;
  } else if (inputElement.type === "date" && isSet(inputElement.valueAsDate)) {
    return inputElement.valueAsDate;
  } else if (inputElement.type === "number") {
    return inputElement.valueAsNumber;
  } else {
    return inputElement.value;
  }
}

export function setInputField(
  inStorage: any,
  inName: string,
  value: string | boolean | number | Date | Record<string, unknown> | undefined | null,
) {
  let isNestedElementModified = false;
  // determine storage element
  let storage = inStorage as { [key: string]: unknown }; // per default, we directly use the provided storage
  let name = inName; // per default, we directly use provided member name
  // remove "]" characters
  name = name.replace("]", "");
  // is name element still nested?
  while (name.includes(".") || name.includes("[")) {
    // we face a nested element
    // array or sub-object?
    const isObj =
      !name.includes("[") || (name.includes(".") && name.indexOf(".") < name.indexOf("["));
    const member = name.split(/[.[]/)[0];
    // recreate this sub-node object to ensure screen updates
    if (isObj) {
      storage[member] = storage.hasOwnProperty(member)
        ? { ...(storage[member] as { [key: string]: unknown }) }
        : {};
    } else {
      storage[member] = Array.isArray(storage[member]) ? [...(storage[member] as [unknown])] : [];
    }
    storage = storage[member] as { [key: string]: unknown };
    name = name.substring(member.length + 1);
    // take this new element as the new basis
    isNestedElementModified = true;
  }

  // check for matching data and storage type
  if (typeof storage[name] === typeof value) {
    storage[name] = value;
  } else if (!isSet(storage[name])) {
    // console.log("initializing new entry for " + name, value);
    storage[name] = value;
  } else if (!isSet(value)) {
    //console.log("clearing entry for " + name);
    storage[name] = value;
  } else {
    console.warn(
      `unsupported member types in setInputField of ${name}: ${typeof storage[
        name
      ]} / ${typeof value}`,
    );
    storage[name] = value;
  }
  return isNestedElementModified;
}

// format given number with thousand separators
export function formatNum(num: number | string) {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

// computed enums with no explicitely set value or non-string typed value
// have maps in both directions, which need to be filtered
export function enumToLabels<TEnum extends string>(enumType: EnumTypeString<TEnum>): TEnum[] {
  return Object.values(enumType).filter((value, index) => index % 2 === 0) as TEnum[];
}
// string enums have only a single map of values
export function stringEnumToLabels<TEnum extends string>(enumType: EnumTypeString<TEnum>): TEnum[] {
  return Object.values(enumType) as TEnum[];
}

export function enumToDropdownItems<TEnum extends string>(
  enumType: EnumTypeString<TEnum>,
  name: string,
  labelPrefix: string,
  mapperFunction: (labelName: string, value: TEnum) => void,
): JSX.Element[] {
  const items = enumToLabels(enumType).map((curItem) => {
    return (
      <Dropdown.Item key={`${curItem}`} onClick={mapperFunction.bind(null, name, curItem)}>
        <FormattedMessage id={`${labelPrefix}${curItem}`} />
      </Dropdown.Item>
    );
  });
  return items;
}

export function toDropdownItems<ItemType>(
  valueList: ItemType[],
  name: string,
  mapperFunction: (name: string, value: ItemType) => void,
  labelFunction?: (value: ItemType) => string | JSX.Element,
  selectedType?: number,
): JSX.Element[] {
  const propertyName = name.substring(name.lastIndexOf(".") + 1);
  const dropdownItems = valueList.map((curValue) => {
    return (
      <Dropdown.Item
        key={`${curValue}${name}`}
        onClick={
          selectedType === undefined || selectedType !== curValue
            ? mapperFunction.bind(null, name, curValue)
            : undefined
        }
      >
        {labelFunction ? (
          labelFunction(curValue)
        ) : (
          <FormattedMessage id={`${propertyName}_${curValue}`} />
        )}
      </Dropdown.Item>
    );
  });
  return dropdownItems;
}

export function rgbToRgba(rgb: string, alpha: number) {
  // do we already have an alpha set?
  if (rgb.indexOf("a") === -1) {
    return rgb.replace(")", `, ${alpha})`).replace("rgb", "rgba");
  }
}
export function hexToRgba(hex: string, alpha: number) {
  // test for valid input string
  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    let colorStr: string[] = hex.substring(1).split("");
    if (colorStr.length === 3) {
      // short form, e.g. #fff, #000
      // extend to long form
      colorStr = [colorStr[0], colorStr[0], colorStr[1], colorStr[1], colorStr[2], colorStr[2]];
    }
    const colorCode = parseInt(colorStr.join(""), 16);
    const colorArr: number[] = [(colorCode >> 16) & 255, (colorCode >> 8) & 255, colorCode & 255];
    colorArr.push(alpha);
    return "rgba(" + colorArr.join(",") + ")";
  } else {
    console.warn("cannot convert color " + hex);
    return hex;
  }
}

export function isStateDirty(state: DataState) {
  return state === DataState.DIRTY || state === DataState.SAVE_REQ || state === DataState.SAVE_FAIL;
}

export function getPeriodStart(
  startDate: number,
  zoom: TimePeriod | undefined,
  timezoneOffset: number,
) {
  const timezoneDifference = getTimezoneDiff(timezoneOffset);
  // the calculation date is shifted in timezone
  const date = new Date((startDate - timezoneDifference) * 1000);

  // fix hours/minutes/...
  date.setHours(0, 0, 0, 0);
  // fix date
  if (zoom === TimePeriod.YEAR) {
    date.setDate(1);
    date.setMonth(0);
  } else if (zoom === TimePeriod.MONTH) {
    date.setDate(1);
  } else if (zoom === TimePeriod.WEEK) {
    // getDay has 0 for Sunday but we want to start the week on Monday
    date.setDate(date.getDate() - ((date.getDay() + 6) % 7));
  }
  return date.getTime() / 1000 + timezoneDifference;
}

export function getEndDate(
  startDate: number,
  zoom: TimePeriod | undefined,
  timezoneOffset: number,
) {
  let endDate = startDate;
  // day, week or month
  // increment more than the formal period to also catch extended periods where summer time shifting appears!
  const days =
    zoom === TimePeriod.DAY
      ? 1.2
      : zoom === TimePeriod.WEEK
      ? 8
      : zoom === TimePeriod.MONTH
      ? 35
      : zoom === TimePeriod.YEAR
      ? 366 // need to work in leap years
      : 0;
  // shift date to one period later
  endDate += days * 24 * 3600;
  // finally, align new end date to nice borders
  const resultingDate = getPeriodStart(endDate, zoom, timezoneOffset);
  return resultingDate;
}

export function getCustomPeriodAggregation(
  timePeriod: TimePeriod,
  startDate: number | undefined,
  endDate: number | undefined,
) {
  const timediff = isSet(endDate) && isSet(startDate) ? endDate - startDate : undefined;

  const aggregation =
    timePeriod === TimePeriod.CUSTOM && isSet(timediff)
      ? timediff > 30 * 24 * 60 * 60
        ? Aggregation.MONTH
        : timediff > 7 * 24 * 60 * 60
        ? Aggregation.DAY
        : timediff > 24 * 60 * 60
        ? Aggregation.HOUR
        : Aggregation.NONE
      : timePeriod === TimePeriod.DAY
      ? Aggregation.NONE
      : timePeriod === TimePeriod.WEEK
      ? Aggregation.HOUR
      : timePeriod === TimePeriod.MONTH
      ? Aggregation.DAY
      : /* zoom === TimePeriod.YEAR */ Aggregation.MONTH;

  return aggregation;
}

export function getGraphBoundaryDates(
  graphStartDate: number | undefined,
  graphEndDate: number | undefined,
  timezoneOffset: number | undefined,
  graphZoom: TimePeriod | undefined,
) {
  // derive timezoneoffset
  if (!isSet(timezoneOffset)) {
    timezoneOffset = -getBrowserTimezoneOffset();
  }
  // if we zoom to all data, we provide undefined timestamps
  if (graphZoom === TimePeriod.ALL) {
    return {
      startDate: undefined,
      endDate: undefined,
      timezoneOffset,
    };
  }

  // we need to clone the start date to be able to detect changes for screen updates
  let startDate: number | undefined;
  if (graphStartDate) {
    // clone date
    startDate = graphStartDate;
  } else {
    // as a fallback, take browser date
    startDate = Date.now() / 1000;
  }
  // adjust to zoom limits
  startDate = getPeriodStart(startDate, graphZoom, timezoneOffset);
  const endDate = isSet(graphEndDate)
    ? graphEndDate
    : getEndDate(startDate, graphZoom, timezoneOffset);

  return {
    startDate,
    endDate,
    timezoneOffset,
  };
}

export function getGraphDateSeries(
  graphStartDate: number | undefined,
  graphEndDate: number | undefined,
  timezoneOffset: number | undefined,
  graphZoom: TimePeriod,
  interval: Duration,
) {
  // get start and end of range
  const graphDates = getGraphBoundaryDates(graphStartDate, graphEndDate, timezoneOffset, graphZoom);
  const dateSeries = [];
  // if we zoom to all data, we provide no timestamps here
  if (graphZoom !== TimePeriod.ALL) {
    let curDate = DateTime.fromSeconds(graphDates.startDate!);
    const endDate = DateTime.fromSeconds(graphDates.endDate!);
    while (curDate < endDate) {
      dateSeries.push(curDate);
      curDate = curDate.plus(interval);
    }
  }
  return {
    ...graphDates,
    dateSeries,
  };
}

export function roundTimestamp(timestamp: number, isOnlyOneSelected: boolean) {
  const roundToMinutes = !isOnlyOneSelected;
  return roundToMinutes ? Math.round(timestamp / 300) * 300 : timestamp;
}

export function getTimestampsForTrendData(
  trendData: ITrendData,
  inStartDate: number | undefined,
  inEndDate: number | undefined,
  timezoneOffset: number | undefined,
  timePeriod: TimePeriod,
  isOnlyOneSelected: boolean,
) {
  const graphDates = getGraphBoundaryDates(inStartDate, inEndDate, timezoneOffset, timePeriod);

  const allTimestamps = Object.keys(trendData)
    .map((item) => parseInt(item))
    .filter((curItem) => curItem >= graphDates.startDate! && curItem < graphDates.endDate!);

  const startDate = graphDates.startDate;
  const endDate = graphDates.endDate;

  let usedTimestamps = allTimestamps.map((item) => roundTimestamp(item, isOnlyOneSelected));
  usedTimestamps = [...new Set(usedTimestamps)]; // remove potential duplicates

  return { startDate, endDate, allTimestamps, usedTimestamps };
}

export const FORMAT_NON_KILO_LIMIT = 1.0; // below this value, the number will be expressed in W instead of kW

export function doKiloConversion(value: number | null | undefined) {
  return isSet(value) && value !== 0 && Math.abs(value) < FORMAT_NON_KILO_LIMIT;
}

export function getPowerUnit(unit: PowerUnit | undefined, doConversion: boolean) {
  if (doConversion) {
    return unit === PowerUnit.KWH
      ? "W"
      : unit === PowerUnit.QM
      ? "dm³/h"
      : unit === PowerUnit.L
      ? "ml/h"
      : unit === PowerUnit.KG
      ? "g/h"
      : unit === PowerUnit.KVARH
      ? "var"
      : unit === PowerUnit.NONE
      ? `milli/h`
      : "milli";
  }
  return unit === PowerUnit.NONE ? "/h" : unit;
}

export function getEnergyUnit(unit: EnergyUnit | PowerUnit | undefined, doConversion: boolean) {
  if (doConversion) {
    return unit === EnergyUnit.KWH || unit === PowerUnit.KWH
      ? "Wh"
      : unit === EnergyUnit.QM || unit === PowerUnit.QM
      ? "dm³"
      : unit === EnergyUnit.L || unit === PowerUnit.L
      ? "ml"
      : unit === EnergyUnit.KG || unit === PowerUnit.KG
      ? "g"
      : unit === EnergyUnit.KVARH || unit === PowerUnit.KVARH
      ? "varh"
      : unit === EnergyUnit.NONE || unit === PowerUnit.NONE
      ? "milli"
      : "milli";
  }
  return unit === EnergyUnit.KWH || unit === PowerUnit.KWH
    ? "kWh"
    : unit === EnergyUnit.QM || unit === PowerUnit.QM
    ? "m³"
    : unit === EnergyUnit.L || unit === PowerUnit.L
    ? "l"
    : unit === EnergyUnit.KG || unit === PowerUnit.KG
    ? "kg"
    : unit === EnergyUnit.KVARH || unit === PowerUnit.KVARH
    ? "kvarh"
    : unit === EnergyUnit.NONE || unit === PowerUnit.NONE
    ? ""
    : "";
}

export function formatPowerValueNumber(value: number | null | undefined): number {
  if (!isSet(value)) {
    return 0; // empty string? 0? "-"?
  }

  // check whether this value should be reported in W instead of kW
  const doConversion = doKiloConversion(value);
  const scaledVal = doConversion ? value * 1000 : value;
  return roundToMinDigits(scaledVal, 2) as number;
}

export function formatPowerValue(value: number | null | undefined, unit: PowerUnit | undefined) {
  if (!isSet(value) || isNaN(value)) {
    return "-"; // empty string? 0? "-"?
  }

  // check whether this value should be reported in W instead of kW
  const doConversion = doKiloConversion(value);
  const scaledVal = doConversion ? value * 1000 : value;
  const scaledUnit = getPowerUnit(unit, doConversion);
  return `${roundToMinDigits(scaledVal, 2)} ${scaledUnit}`;
}

export function formatEnergyValue(
  value: number | null | undefined,
  unit: EnergyUnit | PowerUnit | undefined,
) {
  if (!isSet(value) || isNaN(value)) {
    return "-"; // empty string? 0? "-"?
  }

  // check whether this value should be reported in W instead of kW
  const doConversion = doKiloConversion(value);
  const scaledVal = doConversion ? value * 1000 : value;
  const scaledUnit = getEnergyUnit(unit, doConversion);
  return `${roundToMinDigits(scaledVal, 2)} ${scaledUnit}`;
}

export const parseDateTimeEvent = (
  event: React.FormEvent<HTMLInputElement>,
  oldTimestamp: number | undefined,
  timezoneOffset?: number,
): number | undefined => {
  // we need some special tweeking for the datetime-local input box
  const newTimestamp = parseHtmlInputValue(event.currentTarget);
  let utcTimestamp: number | undefined;
  // is timestamp valid?
  if (event.currentTarget.name === "timestamp") {
    if (isSet(newTimestamp)) {
      if (newTimestamp instanceof Date) {
        utcTimestamp = newTimestamp.getTime() - 1000 * (timezoneOffset ?? 0);
      } else {
        // fallback for browsers not supporting datetime input (currently FF)
        // or if datetime-local input is used
        utcTimestamp = new Date(newTimestamp as string).getTime();
      }
    }
  } else if (event.currentTarget.name === "date") {
    if (isSet(newTimestamp) && newTimestamp instanceof Date) {
      const offsetOnDay = isSet(oldTimestamp) ? oldTimestamp % (24 * 3600 * 1000) : 0;
      const newDate = newTimestamp.getTime() - (newTimestamp.getTime() % (24 * 3600 * 1000));
      utcTimestamp = newDate + offsetOnDay + 1000 * getBrowserTimezoneOffset(); // set to midnight local timezone
    }
  } else if (event.currentTarget.name === "time") {
    // we need to construct the new Date() at the current day, otherwise the
    // hours might be wrong due to winterzeit/sommerzeit
    const dateOffset =
      (oldTimestamp ?? 0) < 24 * 3600 * 1000 ? Date.now() - (Date.now() % (24 * 3600 * 1000)) : 0;
    const oldDate = isSet(oldTimestamp)
      ? new Date(oldTimestamp + dateOffset - getTimezoneDiff(timezoneOffset))
      : new Date();
    const timestring = newTimestamp as string;
    const newDate = new Date(
      oldDate.getFullYear(),
      oldDate.getMonth(),
      oldDate.getDate(),
      parseInt(timestring.substring(0, 2)),
      parseInt(timestring.substring(3, 5)),
      parseInt(timestring.substring(6, 8)) || 0, // take care: in some browsers (Chrome) the string may omitt seconds
    );
    utcTimestamp = newDate.getTime() + getTimezoneDiff(timezoneOffset);
    //console.log("new time", utcTimestamp, oldTimestamp);
  } else {
    console.warn("unsupported time event", event.currentTarget.name);
  }

  if (!utcTimestamp || isNaN(utcTimestamp)) {
    console.warn("parsed illegal timestamp", newTimestamp);
    return undefined;
  }
  return utcTimestamp;
};

export function makeSortHeader(
  table: string,
  column: string,
  caption: JSX.Element,
  curSort: ITableSort | undefined,
  onSortAction: (sort: IActionSortTable) => void,
  addClasses?: string[],
  title?: string,
  colSpan?: number,
) {
  const classes = addClasses ?? [];
  if (curSort?.column === column) {
    classes.push(curSort.isSortAscending ? "sort-asc" : "sort-desc");
  } else {
    classes.push("sort");
  }
  return (
    <th
      className={classes.join(" ")}
      title={title}
      onClick={() =>
        onSortAction({
          table,
          column,
          isSortAscending: column !== curSort?.column ? true : !curSort?.isSortAscending,
        })
      }
      colSpan={colSpan}
    >
      {caption}
    </th>
  );
}

export function tabClickHandler(event: React.KeyboardEvent<HTMLElement>) {
  if (event.key === "Enter") {
    event.currentTarget.click();
  }
}

export function isOcppFrontend(rootPath: RootPath | undefined) {
  // we use the /ocpp path and EP uses mobile.enline.at
  return (
    IS_CLOUD && (rootPath === RootPath.OCPP || window.location.hostname === "mobile.enline.at")
  );
}

export function isLocalhost() {
  return Boolean(
    window.location.hostname === "localhost" ||
      // [::1] is the IPv6 localhost address.
      window.location.hostname === "[::1]" ||
      // 127.0.0.1/8 is considered localhost for IPv4.
      window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/),
  );
}

export function getTheme() {
  switch (window.location.hostname) {
    case "enline.embyt.com":
    case "empower.embyt.com":
    default:
      return Theme.EMBYT;
    case "localhost":
    case "cloud.enline.at":
    case "mobile.enline.at":
    case "enline.cloud":
    case "www.enline.cloud":
    case "dev.embyt.com":
      return Theme.EPM;
  }
}

export function makeCommandSeqNr(timestampMs?: number) {
  // is timestamp given or use current time?
  const value = timestampMs ?? Date.now();
  // full ms is too long for type int, so take some module
  return value % (1 << 30);
}

export function getBrowserName() {
  const userAgent = navigator.userAgent;
  let browserName;

  if (userAgent.match(/chrome|chromium|crios/i)) {
    browserName = "chrome";
  } else if (userAgent.match(/firefox|fxios/i)) {
    browserName = "firefox";
  } else if (userAgent.match(/safari/i)) {
    browserName = "safari";
  } else if (userAgent.match(/opr\//i)) {
    browserName = "opera";
  } else if (userAgent.match(/edg/i)) {
    browserName = "edge";
  } else {
    browserName = "unsupported Browser (" + userAgent + ")";
  }

  return browserName;
}

export function isSafariBrowser() {
  return getBrowserName() === "safari";
}

export function isInternetExplorer() {
  return getBrowserName() === "edge";
}

export function getIoType(state: IState) {
  if (isSet(state.gui.selectedIoType)) {
    return state.gui.selectedIoType;
  }
  // do we have digital inputs or outputs?
  if (
    state.parameter?.output.some(
      (curItem) => curItem.webActive && curItem.mode !== OutputMode.ANALOG,
    ) ||
    state.parameter?.input.some((curItem) => curItem.webActive && curItem.type !== InputType.ANALOG)
  ) {
    return "digital";
  }
  return "analog";
}

export function getTrendIoGraphViewMode(
  selection: IPanelSelection | undefined,
  selectedIoType: "digital" | "analog",
  allDataAvailable: boolean,
  prepareTrendData: boolean,
): GraphViewMode | undefined {
  let graphViewMode: GraphViewMode | undefined = selection?.graphViewMode;

  // if the user selected TABLE-View --> just leave the setting as is...
  if (graphViewMode !== GraphViewMode.TABLE) {
    // check if we prepare for TrendData or IO-Data
    if (prepareTrendData) {
      // on TrendData -> HORIZONTAL is not allowed
      if (graphViewMode === GraphViewMode.DIGITAL) {
        // use BAR instead
        graphViewMode = GraphViewMode.BAR;
      }
    } else {
      // otherwise -> check which Type we should show:
      if (selectedIoType === "analog") {
        // on ANALOG signals -> we just show LINE-Chart
        // independent on aggregated data or full!
        graphViewMode = GraphViewMode.LINE;
      } else {
        // on DIGITAL signals
        // check if we got all data:
        if (allDataAvailable) {
          // yes -> so show the horizontal (timeline-)view
          graphViewMode = GraphViewMode.DIGITAL;
        } else {
          // otherwise -> check which setting currently is used:
          // allowed values: BAR or LINE -> if the user switched from
          // all data (e.g. DAY) to aggregated data (e.g. MONTH), the setting
          // might be HORIZONTAL -> then we should switch to BAR (default)
          if (graphViewMode === GraphViewMode.DIGITAL) {
            graphViewMode = GraphViewMode.BAR;
          }
          // otherwise it is LINE or BAR already -> both are allowd, so levae them
        }
      }
    }
  }
  return graphViewMode;
}

export function getGraphMinUnits(timespan: number): TimeUnit {
  let minUnits: TimeUnit = "day";

  if (timespan > GraphTickTimeSpans.MONTHS) {
    minUnits = "month";
  } else if (timespan < GraphTickTimeSpans.DAYS) {
    if (timespan < GraphTickTimeSpans.HOURS) {
      if (timespan < GraphTickTimeSpans.MINUTES) {
        minUnits = "minute";
      } else {
        minUnits = "hour";
      }
    }
  }
  // all other else-paths would return "day"!!
  return minUnits;
}

export const getSite = (state: IState) =>
  state.site.siteList?.find((curSite) => curSite.id === state.cached.selectedSite);

const getUserAccess = (state: IState) =>
  state.site.userList?.find((curItem) => curItem.secId === state.site.user?.secId)?.accessRights;
const getHasOcppChargers = (state: IState) =>
  state.ocpp.charger.filter((curItem) => isSet(curItem.fk_charger)).length > 0;
const getHasEnlineChargerData = (state: IState) =>
  (state.parameter?.charger.length ?? 0) > 0 && (state.data?.charger?.length ?? 0) > 0;

export const getPanels = createSelector(
  [
    (state: IState) => state.parameter,
    getSite,
    getUserAccess,
    (state: IState) => state.router.rootPath,
    (state: IState) => !!state.site.user?.isRfidUser,
    (state: IState) => isSet(state.scheduler.allData.lastData),
    getHasOcppChargers,
    getHasEnlineChargerData,
    (state: IState) => state.ocpp.pendingChargingSession.length > 0,
    (state: IState) => state.ocpp.rfid,
    (state: IState) => state.data.alarms.length > 0,
  ],
  (
    parameter,
    site,
    accessRights,
    rootPath,
    isRfidUser,
    hasLiveData,
    hasOcppChargers,
    hasEnlineChargerData,
    hasPendingChargingSessions,
    rfidCards,
    hasAlarms,
  ) => {
    const isOcppRestricted = isOcppFrontend(rootPath);
    const isTrendRestricted = rootPath === RootPath.TREND;

    const panels: INavigationPanelEntry[] = [];
    const dashboards: IDashboardEntry[] = [];
    let hasReports = false;

    let nrPanels = 0;
    if (!isRfidUser && hasLiveData && !isOcppRestricted) {
      panels.push({ heading: MainPanel.DASHBOARD });
      panels.push({ heading: MainPanel.POWER, subPanels: [MainPanel.ENERGYMETERS] });
      nrPanels += 2;
      dashboards.push({ entry: DashboardPanel.POWER, prio: 5 });
      if (parameter?.regulation.length) {
        panels.push({ heading: MainPanel.MONITOR });
        dashboards.push({ entry: DashboardPanel.MONITOR, prio: 0 });
        nrPanels += 1;
      }
    }

    if (!isOcppRestricted) {
      const trendPanels: INavigationPanelEntry[] = [];
      if (parameter?.tariffline && parameter?.tariffline.length > 0) {
        trendPanels.push({ heading: MainPanel.ENERGY });
        nrPanels += 1;
      }
      if (
        (site?.cloudSettings?.trendMonitor?.filter((curItem) => curItem.isActive).length ?? 0) > 0
      ) {
        trendPanels.push({ heading: MainPanel.TRENDMONITOR });
        dashboards.push({ entry: DashboardPanel.TRENDMONITOR, prio: 7 });
        nrPanels += 1;
      }
      if (
        (site?.cloudSettings?.reports?.filter(
          (curItem) => curItem.isActive && curItem.type !== "site",
        ).length ?? 0) > 0
      ) {
        trendPanels.push({ heading: MainPanel.REPORT });
        hasReports = true;
        nrPanels += 1;
      }
      if (
        site?.cloudSettings?.customDashboard ||
        parameter?.general.customSettings?.customDashboard
      ) {
        trendPanels.push({ heading: MainPanel.PUBLIC_DISPLAY });
        nrPanels += 1;
      }
      panels.push({
        heading: MainPanel.ENERGY,
        childPanels: trendPanels,
      });
      dashboards.push({ entry: DashboardPanel.ENERGY, prio: 1 });
    }

    if (!isOcppRestricted) {
      const journalPanels: INavigationPanelEntry[] = [];
      // check wether we have IOs at all
      const hasInput = parameter?.input.some((curItem) => curItem.webActive);
      const hasOutput = parameter?.output.some((curItem) => curItem.webActive);
      if (hasLiveData && !isOcppRestricted && (hasInput || hasOutput)) {
        journalPanels.push({ heading: MainPanel.IOS });
        nrPanels += 1;
      }
      // check wether we have trended IOs
      if (hasInput || hasOutput) {
        journalPanels.push({ heading: MainPanel.IO_TREND });
        nrPanels += 1;
      }
      panels.push({
        heading: MainPanel.IOS,
        childPanels: journalPanels,
      });

      if (hasInput) {
        dashboards.push({ entry: DashboardPanel.INPUTS, prio: 6 });
      }
      if (hasOutput) {
        dashboards.push({ entry: DashboardPanel.OUTPUTS, prio: 3 });
      }
    }

    if (!isTrendRestricted) {
      const chargerPanels: INavigationPanelEntry[] = [];
      if (IS_CLOUD && hasOcppChargers) {
        chargerPanels.push({
          heading: MainPanel.CHARGERLIST,
          subPanels: [MainPanel.CHARGERDETAIL],
        });
        nrPanels += 1;
      }
      if ((!IS_CLOUD || accessRights?.showEnlineChargers) && hasEnlineChargerData) {
        chargerPanels.push({ heading: MainPanel.ENLINE_CHARGER });
        nrPanels += 1;
      }
      // currently active charging session for RFID user?
      if (isRfidUser && hasPendingChargingSessions) {
        chargerPanels.push({ heading: MainPanel.CHARGINGSESSION });
        nrPanels += 1;
      }
      if (hasOcppChargers || isRfidUser) {
        // if we face multiple RFID users, we take the rfid panel, otherwise the plain session list
        if (rfidCards.length > 1) {
          chargerPanels.push({
            heading: MainPanel.RFIDLIST,
            subPanels: [MainPanel.RFIDDETAIL],
          });
        } else {
          chargerPanels.push({ heading: MainPanel.CHARGINGSESSIONLIST });
        }
        nrPanels += 1;
      }
      if (hasOcppChargers && (accessRights?.ocppSettings ?? false)) {
        chargerPanels.push({ heading: MainPanel.OCPPCONFIGURATION });
        nrPanels += 1;
      }
      if (chargerPanels.length > 0) {
        panels.push({
          heading: MainPanel.CHARGERLIST,
          childPanels: chargerPanels,
          subPanels: [MainPanel.CHARGINGSESSION],
        });
        // do not add dashboard panel on local controller webside as it works with ocpp chargers only
        // also, do not add the dashboard panel if there are only enline chargers
        if (IS_CLOUD && hasOcppChargers) {
          dashboards.push({ entry: DashboardPanel.EMOBILTY, prio: 2 });
        }
      }
    }
    // allow charger trends also for OCPP restricted access
    if (
      isOcppRestricted &&
      (site?.cloudSettings?.reports?.filter(
        (curItem) => curItem.isActive && curItem.type === "charger",
      ).length ?? 0) > 0
    ) {
      panels.push({ heading: MainPanel.REPORT });
      hasReports = true;
      nrPanels += 1;
    }

    if (!isOcppRestricted && !isTrendRestricted && !isRfidUser && (hasLiveData || hasAlarms)) {
      panels.push({ heading: MainPanel.ALARMS });
      nrPanels += 1;
      dashboards.push({ entry: DashboardPanel.ALARMS, prio: hasAlarms ? 4 : 10 });
    }

    if (hasReports) {
      dashboards.push({ entry: DashboardPanel.REPORTS, prio: 8 });
    }

    dashboards.push({ entry: DashboardPanel.SITEINFOS, prio: 9 });

    return { panels, nrPanels, dashboards };
  },
);

export function hasLocalSettingsPage(controllerState: IStateController | undefined) {
  return (
    !IS_CLOUD &&
    (isLocalhost() ||
      (isSet(controllerState?.unlockedUntil) && controllerState!.unlockedUntil > Date.now()))
  );
}

function getPriorizedDashboardItems(
  available: IDashboardEntry[],
  used: (DashboardPanel | undefined)[],
) {
  const nextPanel = available
    .sort((a, b) => {
      return a.prio - b.prio;
    })
    .map((item) => item.entry)
    .find((curItem) => !used.includes(curItem));
  return nextPanel;
}

export function getActiveDashboardItems(state: IState) {
  const { panels, nrPanels, dashboards } = getPanels(state);

  const activeDashboardItems: (DashboardPanel | undefined)[] = [];
  const dashboardItems = getSiteSelection(state)?.dashboardPanel;
  for (let i = 0; i < MAX_DASHBOARDS; i++) {
    if (isSet(dashboardItems?.[i])) {
      activeDashboardItems[i] = dashboardItems[i]!;
    } else {
      // take next panel according priorities
      activeDashboardItems[i] = getPriorizedDashboardItems(dashboards, activeDashboardItems);
    }
  }

  return { dashboards, activeDashboardItems };
}

export function getSiteSelection(state: IState) {
  // site selected?
  const siteId = state.cached.selectedSite;
  if (
    !isSet(siteId) ||
    !isSet(state.cached.siteSelection) ||
    !isSet(state.cached.siteSelection[siteId])
  ) {
    return undefined;
  }
  return state.cached.siteSelection[siteId];
}

export function getPanelSelection(state: IState, panel: DashboardPanel, dashboardIndex?: number) {
  // determine dashboard index to use
  let index: number;
  if (isSet(dashboardIndex)) {
    index = dashboardIndex;
  } else if (isSet(state.router.widget)) {
    index = state.router.widget;
  } else {
    const { dashboards, activeDashboardItems } = getActiveDashboardItems(state);
    index = Math.max(activeDashboardItems.indexOf(panel), 0); // if no panel found: -1 -> 0
  }
  const selection = getSiteSelection(state)?.panelSelection[index];
  return { index, selection };
}

export function formatDate(
  timestampMs: number,
  timePeriod: TimePeriod,
  locales?: string | string[],
  showTime?: boolean,
) {
  let options: Intl.DateTimeFormatOptions;
  if (timePeriod === TimePeriod.YEAR) {
    options = {
      year: "numeric",
    };
  } else if (timePeriod === TimePeriod.MONTH) {
    options = {
      year: "numeric",
      month: "long",
    };
  } else if (timePeriod === TimePeriod.WEEK || timePeriod === TimePeriod.CUSTOM) {
    options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      weekday: "short",
    };
  } else {
    // (this.props.xZoom === TimePeriod.DAY)
    options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      weekday: "long",
    };
  }

  if (showTime === true && (timePeriod === TimePeriod.DAY || timePeriod === TimePeriod.WEEK)) {
    options.hour = "numeric";
    options.minute = "numeric";
  }

  const date = new Date(timestampMs);
  return date.toLocaleString(locales, options);
}

export function getDateString(
  timePeriod: TimePeriod,
  startDate: number | undefined,
  endDate: number | undefined,
  isAtEnd: boolean,
  dateString: string,
  locales?: string | string[],
): string | undefined {
  // if we zoom all data, we have no period
  if (timePeriod === TimePeriod.ALL || !isSet(startDate) || !isSet(endDate)) {
    return undefined;
  }
  if (isAtEnd) {
    return dateString;
  }
  const startDateString = formatDate(startDate * 1000, timePeriod, locales);
  if ([TimePeriod.YEAR, TimePeriod.MONTH, TimePeriod.DAY].includes(timePeriod)) {
    return startDateString;
  }
  // (inPeriod === TimePeriod.WEEK || inPeriod === TimePeriod.CUSTOM)
  const endDateString = formatDate(endDate * 1000, timePeriod, locales);
  return `${startDateString} - ${endDateString}`;
}

export function getEnergySourceName(source: EnergySource | undefined) {
  if (source === "electrical" || !isSet(source)) return <FormattedMessage id="Electrical" />;
  if (source === "water") return <FormattedMessage id="Water" />;
  if (source === "gas") return <FormattedMessage id="Gas" />;
  return <>{source}</>;
}

export function id2Index(list: { id?: number }[] | undefined, id: number | undefined | null) {
  if (!isSet(id) || !isSet(list)) {
    return undefined;
  }
  const index = list.findIndex(
    (curItem, curIndex) =>
      (isSet(curItem.id) && curItem.id === id) || (!isSet(curItem.id) && curIndex === id),
  );
  if (index === -1) return undefined;
  return index;
}

// keep consistent with dbutils.ts
export function parseVirtualSiteId(virtSiteId: number) {
  return {
    siteId: virtSiteId < 10000 ? undefined : Math.floor(virtSiteId / 10000),
    itemId: virtSiteId % 10000,
  };
}
